import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Loader2, X, ImagePlus, Sparkles, AlertCircle } from "lucide-react";

const MARKETPLACES = [
    { id: "amazon", label: "Amazon" },
    { id: "shopify", label: "Shopify" },
    { id: "etsy", label: "Etsy" },
    { id: "ebay", label: "eBay" },
];

const TONES = ["persuasive", "playful", "luxury", "minimal", "technical"];

function fileToDataUrl(file) {
    return new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = reject;
        r.readAsDataURL(file);
    });
}

export default function Generate() {
    const { user, refresh } = useAuth();
    const navigate = useNavigate();
    const [images, setImages] = useState([]);
    const [form, setForm] = useState({
        title: "",
        category: "",
        marketplace: "amazon",
        key_features: "",
        target_keywords: "",
        variants: "",
        tone: "persuasive",
    });
    const [loading, setLoading] = useState(false);

    const onFiles = async (e) => {
        const files = Array.from(e.target.files || []).slice(0, 4 - images.length);
        const arr = [];
        for (const f of files) {
            if (!["image/jpeg", "image/png", "image/webp"].includes(f.type)) {
                toast.error(`Unsupported file: ${f.name}. Use JPG/PNG/WEBP.`);
                continue;
            }
            if (f.size > 5 * 1024 * 1024) {
                toast.error(`${f.name} is too large (max 5MB)`);
                continue;
            }
            const url = await fileToDataUrl(f);
            arr.push({ name: f.name, url });
        }
        setImages((prev) => [...prev, ...arr].slice(0, 4));
        e.target.value = "";
    };

    const removeImage = (i) => setImages((prev) => prev.filter((_, idx) => idx !== i));

    const submit = async (e) => {
        e.preventDefault();
        if (images.length === 0) return toast.error("Add at least one product image");
        if (!form.title.trim()) return toast.error("Title is required");
        if ((user?.credits ?? 0) <= 0) {
            toast.error("You're out of credits");
            return navigate("/pricing");
        }
        setLoading(true);
        try {
            const payload = {
                title: form.title,
                category: form.category || "general",
                marketplace: form.marketplace,
                key_features: form.key_features.split(",").map((s) => s.trim()).filter(Boolean),
                target_keywords: form.target_keywords.split(",").map((s) => s.trim()).filter(Boolean),
                variants: form.variants.split(",").map((s) => s.trim()).filter(Boolean),
                tone: form.tone,
                images_base64: images.map((i) => i.url),
            };
            const { data } = await api.post("/generate", payload);
            toast.success("Listing ready.");
            await refresh();
            navigate(`/results/${data.id}`);
        } catch (err) {
            const detail = err.response?.data?.detail || "Generation failed";
            toast.error(detail);
            if (err.response?.status === 402) navigate("/pricing");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="max-w-5xl mx-auto px-6 py-12" data-testid="generate-page">
            <div className="mb-10">
                <div className="overline mb-2">Create</div>
                <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight">New listing<span className="text-accent">.</span></h1>
                <p className="text-zinc-500 mt-2">Upload product photos and a few specs. We&apos;ll do the rest.</p>
            </div>

            {(user?.credits ?? 0) <= 0 && (
                <div className="surface p-4 mb-6 flex items-center gap-3 border-amber-500/30 bg-amber-500/5" data-testid="no-credits-banner">
                    <AlertCircle className="w-5 h-5 flex-shrink-0 text-amber-500" />
                    <div className="flex-1">
                        <div className="font-medium text-zinc-100">You&apos;re out of credits.</div>
                        <div className="text-sm text-zinc-500">Grab a pack to keep generating.</div>
                    </div>
                    <button onClick={() => navigate("/pricing")} className="btn-primary text-sm" data-testid="buy-credits-banner">Buy</button>
                </div>
            )}

            <form onSubmit={submit} className="grid md:grid-cols-12 gap-6">
                {/* Images */}
                <div className="md:col-span-5">
                    <div className="surface p-6">
                        <label className="overline block mb-4">Product images ({images.length}/4)</label>
                        <div className="grid grid-cols-2 gap-3 mb-4">
                            {images.map((img, i) => (
                                <div key={img.url} className="relative aspect-square rounded-md border border-white/10 bg-surface-2 overflow-hidden">
                                    <img src={img.url} alt={img.name} className="w-full h-full object-cover" />
                                    <button type="button" onClick={() => removeImage(i)} className="absolute top-1.5 right-1.5 bg-bg/90 backdrop-blur border border-white/10 rounded-md w-7 h-7 flex items-center justify-center hover:bg-bg" data-testid={`remove-image-${i}`}>
                                        <X className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            ))}
                            {images.length < 4 && (
                                <label className="aspect-square rounded-md border border-dashed border-white/15 bg-surface-2 flex flex-col items-center justify-center cursor-pointer hover:border-accent/50 hover:bg-accent/[0.02] transition-colors" data-testid="upload-zone">
                                    <input type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" onChange={onFiles} data-testid="image-input" />
                                    <ImagePlus className="w-6 h-6 mb-1 text-zinc-500" strokeWidth={1.75} />
                                    <span className="font-mono text-xs text-zinc-500">Add image</span>
                                </label>
                            )}
                        </div>
                        <div className="font-mono text-xs text-zinc-600">JPG · PNG · WEBP — max 5MB each</div>
                    </div>
                </div>

                {/* Specs */}
                <div className="md:col-span-7">
                    <div className="surface p-6 space-y-5">
                        <div>
                            <label className="block text-xs font-medium text-zinc-400 mb-2">Product title *</label>
                            <input className="surface-input" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Handmade Ceramic Coffee Mug" data-testid="form-title" />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-medium text-zinc-400 mb-2">Category</label>
                                <input className="surface-input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Home & Kitchen" data-testid="form-category" />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-zinc-400 mb-2">Tone</label>
                                <select className="surface-input" value={form.tone} onChange={(e) => setForm({ ...form, tone: e.target.value })} data-testid="form-tone">
                                    {TONES.map((t) => <option key={t} value={t} className="bg-bg">{t}</option>)}
                                </select>
                            </div>
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-zinc-400 mb-2">Primary marketplace</label>
                            <div className="flex flex-wrap gap-2">
                                {MARKETPLACES.map((m) => (
                                    <button
                                        type="button"
                                        key={m.id}
                                        onClick={() => setForm({ ...form, marketplace: m.id })}
                                        className={`px-4 py-2 rounded-md border text-sm font-medium transition-colors ${
                                            form.marketplace === m.id
                                                ? "bg-accent/10 border-accent/40 text-accent"
                                                : "bg-surface-2 border-white/10 text-zinc-400 hover:text-zinc-100 hover:border-white/20"
                                        }`}
                                        data-testid={`marketplace-${m.id}`}
                                    >
                                        {m.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-zinc-400 mb-2">Key features <span className="text-zinc-600">(comma-separated)</span></label>
                            <input className="surface-input" value={form.key_features} onChange={(e) => setForm({ ...form, key_features: e.target.value })} placeholder="Handmade, Dishwasher safe, 12oz" data-testid="form-features" />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-zinc-400 mb-2">Target keywords <span className="text-zinc-600">(optional)</span></label>
                            <input className="surface-input" value={form.target_keywords} onChange={(e) => setForm({ ...form, target_keywords: e.target.value })} placeholder="ceramic mug, artisan coffee cup" data-testid="form-keywords" />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-zinc-400 mb-2">Variants <span className="text-zinc-600">(optional)</span></label>
                            <input className="surface-input" value={form.variants} onChange={(e) => setForm({ ...form, variants: e.target.value })} placeholder="Cream, Sage, Terracotta" data-testid="form-variants" />
                        </div>
                        <button type="submit" disabled={loading} className="btn-primary w-full" data-testid="generate-submit">
                            {loading ? (<><Loader2 className="w-4 h-4 animate-spin" /> Crafting your listing...</>) : (<><Sparkles className="w-4 h-4" /> Generate listing · 1 credit</>)}
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
}
